import React, { lazy, Suspense } from "react";
import { REWARD_SHOP_ENABLED } from "../constants/rewardShop";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import Router from "./GuardedBrowserRouter";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";
import { useTheme } from "../theme/ThemeContext";
import RouteErrorBoundary from "../systemReport/RouteErrorBoundary";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";
import "./MobileNavFix.scss";
import "../components/assets/scss/BrowserCompatibility.scss";
import Login from "../components/Pages/Login";
import Signup from "../components/Pages/Signup";
import Showcase from "../components/Pages/Showcase";
import InstallGuide from "../components/Pages/InstallGuide";
import Links from "../components/Pages/Links";
import BasicReading from "../components/Pages/BasicReading";
import LinkAdmin from "../components/Pages/LinkAdmin";
import Playlist from "../components/fragment/Playlist";
import Containerfull from "../components/fragment/Containerfull";
import AddMusic from "../components/Pages/AddMusicV3";
import NotFound from "../components/Pages/NotFound";
import AccountManagement from "../components/Pages/AccountManagement";
import AcademyStudentCsvImport from "../components/Pages/AcademyStudentCsvImport";
import BillingResult from "../components/Pages/BillingResult";
import LearningLeaderboard from "../components/Pages/LearningLeaderboard";
import RewardsAdmin from "../components/Pages/RewardsAdmin";
import ApiUsageAdmin from "../components/Pages/ApiUsageAdmin";
import MembershipAdmin from "../components/Pages/MembershipAdmin";
import LevelAdmin from "../components/Pages/LevelAdmin";
import CatalogAdmin from "../components/Pages/CatalogAdmin";
import AcademyInviteSignup from "../components/Pages/AcademyInviteSignup";
import ForgotPassword from "../components/Pages/ForgotPassword";
import AcademyStudentSetup from "../components/Pages/AcademyStudentSetup";
import AccountSecurity from "../components/Pages/AccountSecurity";
import StudentSettings from "../components/Pages/StudentSettings";
import StudentOnboarding from "../components/Pages/StudentOnboarding";
import StudentNotifications from "../components/Pages/StudentNotifications";
import StoreSalesPaused from "../components/Pages/StoreSalesPaused";
import { PUBLIC_MATERIAL_SALES_ENABLED } from "../constants/commerceAvailability";
import StoreAuthPage from "../components/Pages/StoreAuthPage";
import StoreVerificationPage from "../components/Pages/StoreVerificationPage";
import StoreCart from "../components/Pages/StoreCart";
import StoreCheckout from "../components/Pages/StoreCheckout";
import StoreOrders from "../components/Pages/StoreOrders";
import StorePaymentResult from "../components/Pages/StorePaymentResult";
import StorePasswordPage from "../components/Pages/StorePasswordPage";
import AdminStoreOrders from "../components/Pages/AdminStoreOrders";
import AdminClassMaterials from "../components/Pages/AdminClassMaterials";
import AdminMaterialPackages from "../components/Pages/AdminMaterialPackages";
import AdminStudentLifecycle from "../components/Pages/AdminStudentLifecycle";
import SpeakingContentAdmin from "../components/Pages/SpeakingContentAdmin";
import Support from "../components/Pages/Support";
import AdminSupport from "../components/Pages/AdminSupport";
import { AuthProvider } from "../auth/AuthContext";
import ThemeRoleBridge from "../theme/ThemeRoleBridge";
import ProtectedRoute from "../auth/ProtectedRoute";
import RoleHomeRedirect from "../auth/RoleHomeRedirect";
import { StoreProvider } from "../store/StoreContext";
import publicSeo from "../config/publicSeo.json";
import RouteScrollToTop from "./RouteScrollToTop";
import SpeakingRecognizerWarmup from "./SpeakingRecognizerWarmup";
import { APP_ROUTER_FUTURE } from "./routerFuture";

const ManagementDashboard = lazy(() => import("../components/Pages/ManagementDashboard"));
const AIMaterialGenerator = lazy(() => import("../components/Pages/AIMaterialGenerator"));
const ConversationPractice = lazy(() => import("../components/Pages/ConversationPractice"));
const PronunciationCoach = lazy(() => import("../components/Pages/PronunciationCoach"));
const TextbookSpeakingChallenge = lazy(() => import("../components/Pages/TextbookSpeakingChallenge"));
const TeacherAssignments = lazy(() => import("../components/Pages/TeacherAssignments"));
const StudentAssignments = lazy(() => import("../components/Pages/StudentAssignments"));
const TeacherAttendance = lazy(() => import("../components/Pages/TeacherAttendance"));
const StudentMakeup = lazy(() => import("../components/Pages/StudentMakeup"));
const AdminCreateStaff = lazy(() => import("../components/Pages/AdminCreateStaff"));
const ReviewCenter = lazy(() => import("../components/Pages/ReviewCenter"));
const WeeklyReport = lazy(() => import("../components/Pages/WeeklyReport"));
const MembershipCenter = lazy(() => import("../components/Pages/MembershipCenter"));
const Rewards = lazy(() => import("../components/Pages/Rewards"));
const TrialLesson = lazy(() => import("../components/Pages/TrialLesson"));
const StudentMaterials = lazy(() => import("../components/Pages/StudentMaterials"));
const ParentGuide = lazy(() => import("../components/Pages/ParentGuide"));
const TeamBossSpeakingPreview = lazy(() => import("../components/Pages/TeamBossSpeakingPreview/Preview"));

const LegacyPlaylistRedirect = () => {
    const { playlistId } = useParams();
    return <Navigate to={`/student/books/${playlistId}`} replace />;
};

const RouteSeoPolicy = () => {
    const { pathname } = useLocation();
    const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const isIndexable = Boolean(publicSeo[normalizedPath]);
    const policy = isIndexable ? "index,follow" : "noindex,nofollow";

    return (
        <Helmet>
            <meta name="robots" content={policy} />
            <meta name="googlebot" content={policy} />
        </Helmet>
    );
};

const AppRoutePlaceholder = () => (
    <section className="app-route-placeholder" aria-label="正在準備頁面" aria-busy="true">
        <span>—</span>
        <span>—</span>
        <span>—</span>
    </section>
);

const AuthenticatedAppLayout = () => (
    <ProtectedRoute>
        <Containerfull>
            <Suspense fallback={<AppRoutePlaceholder />}>
                <Outlet />
            </Suspense>
        </Containerfull>
    </ProtectedRoute>
);

const App = () => {
    const { resolved: resolvedTheme } = useTheme();
    return (
        <Router future={APP_ROUTER_FUTURE}>
            <RouteScrollToTop />
            <SpeakingRecognizerWarmup />
            <AuthProvider>
                <ThemeRoleBridge />
                <StoreProvider>
                <ToastContainer
                    position="top-center"
                    autoClose={2000}
                    limit={2}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover={false}
                    theme={resolvedTheme}
                />

                <Helmet>
                    <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png?v=lion1" />
                    <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png?v=lion1" />
                    <link rel="shortcut icon" type="image/x-icon" href="/favicon.ico?v=lion1" />
                    <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon-v3.png" />
                    <meta name="theme-color" content="#073763" />
                </Helmet>

                <RouteSeoPolicy />

                <RouteErrorBoundary>
                <Routes>
                    <Route path="/" element={<Showcase />} />
                    <Route path="/install" element={<InstallGuide />} />
                    <Route path="/try" element={<TrialLesson />} />
                    <Route path="/parents" element={<ParentGuide />} />
                    <Route path="/links" element={<Links />} />
                    <Route path="/basic-reading" element={<BasicReading />} />
                    <Route path="/basic-reading/:level" element={<BasicReading />} />
                    <Route path="/home" element={<Navigate to="/" replace />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/solve" element={<Navigate to="/forgot-password" replace />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/academy/invite" element={<AcademyInviteSignup />} />
                    <Route path="/academy/activate" element={<AcademyInviteSignup manualEntry />} />
                    <Route path="/academy/student-setup" element={<AcademyStudentSetup />} />
                    <Route path="/academy/recover" element={<AcademyStudentSetup recoveryOnly />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/showcase" element={<Navigate to="/" replace />} />
                    <Route path="/freetrial" element={<Navigate to="/" replace />} />
                    <Route path="/materials" element={<Navigate to="/" replace />} />
                    <Route path="/shop" element={<Navigate to="/" replace />} />
                    <Route path="/shop/cart" element={PUBLIC_MATERIAL_SALES_ENABLED ? <StoreCart /> : <StoreSalesPaused />} />
                    <Route path="/shop/login" element={<StoreAuthPage />} />
                    <Route path="/shop/register" element={<StoreAuthPage register />} />
                    <Route path="/shop/verified" element={<StoreVerificationPage />} />
                    <Route path="/shop/forgot-password" element={<StorePasswordPage />} />
                    <Route path="/shop/reset-password" element={<StorePasswordPage update />} />
                    <Route path="/shop/checkout" element={PUBLIC_MATERIAL_SALES_ENABLED ? <StoreCheckout /> : <StoreSalesPaused />} />
                    <Route path="/shop/orders" element={<StoreOrders />} />
                    <Route path="/shop/orders/:orderNumber" element={<StoreOrders />} />
                    <Route path="/shop/payment/success" element={<StorePaymentResult />} />

                    <Route path="/student/onboarding" element={<ProtectedRoute allowedRoles={["student"]} allowsIncompleteOnboarding><StudentOnboarding /></ProtectedRoute>} />
                    <Route element={<AuthenticatedAppLayout />}>
                        <Route path="/student/dashboard" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><Navigate to="/student/settings" replace /></ProtectedRoute>} />
                        <Route path="/student/assignments" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><StudentAssignments /></ProtectedRoute>} />
                        <Route path="/student/makeup" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><StudentMakeup /></ProtectedRoute>} />
                        <Route path="/student/review" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><ReviewCenter /></ProtectedRoute>} />
                        <Route path="/student/weekly-report" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><WeeklyReport /></ProtectedRoute>} />
                        <Route path="/student/membership" element={<ProtectedRoute allowedRoles={["student"]}><MembershipCenter /></ProtectedRoute>} />
                        <Route path="/student/settings" element={<ProtectedRoute allowedRoles={["student"]}><StudentSettings /></ProtectedRoute>} />
                        <Route path="/student/profile" element={<ProtectedRoute allowedRoles={["student"]}><StudentSettings view="profile" /></ProtectedRoute>} />
                        <Route path="/student/notification-settings" element={<ProtectedRoute allowedRoles={["student"]}><StudentSettings view="notifications" /></ProtectedRoute>} />
                        <Route path="/student/notifications" element={<ProtectedRoute allowedRoles={["student"]}><StudentNotifications /></ProtectedRoute>} />
                        <Route path="/student/friends" element={<Navigate to="/student/settings" replace />} />
                        <Route path="/student/level" element={<Navigate to="/student/leaderboard" replace />} />
                        <Route path="/student/leaderboard" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><LearningLeaderboard /></ProtectedRoute>} />
                        <Route path="/student/rewards" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership>{REWARD_SHOP_ENABLED ? <Rewards /> : <Navigate to="/student/settings" replace />}</ProtectedRoute>} />
                        <Route path="/student/conversation" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]} requiresActiveMembership><ConversationPractice /></ProtectedRoute>} />
                        <Route path="/student/pronunciation" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><PronunciationCoach /></ProtectedRoute>} />
                        <Route path="/student/speaking-challenges" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]} requiresActiveMembership><TextbookSpeakingChallenge /></ProtectedRoute>} />
                        <Route path="/student/speaking-challenges/book/:bookKey" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]} requiresActiveMembership><TextbookSpeakingChallenge /></ProtectedRoute>} />
                        <Route path="/student/speaking-challenges/:questionSetId" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]} requiresActiveMembership><TextbookSpeakingChallenge /></ProtectedRoute>} />
                        <Route path="/student/ai-generator" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]} requiresActiveMembership><AIMaterialGenerator /></ProtectedRoute>} />
                        <Route path="/student/books" element={<ProtectedRoute allowedRoles={["student"]} requiresActiveMembership><StudentMaterials /></ProtectedRoute>} />
                        <Route path="/student/books/:playlistId" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]} requiresActiveMembership><Playlist /></ProtectedRoute>} />
                        <Route path="/billing/success" element={<ProtectedRoute allowedRoles={["student"]}><BillingResult /></ProtectedRoute>} />
                        <Route path="/billing/cancel" element={<ProtectedRoute allowedRoles={["student"]}><BillingResult cancelled /></ProtectedRoute>} />
                        <Route path="/account/security" element={<ProtectedRoute allowedRoles={["student", "teacher", "admin"]}><AccountSecurity /></ProtectedRoute>} />

                        <Route path="/teacher/dashboard" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><ManagementDashboard /></ProtectedRoute>} />
                        <Route path="/teacher/reports" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><WeeklyReport /></ProtectedRoute>} />
                        <Route path="/teacher/assignments" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><TeacherAssignments /></ProtectedRoute>} />
                        <Route path="/teacher/attendance" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><TeacherAttendance /></ProtectedRoute>} />
                        <Route path="/teacher/accounts" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><AccountManagement /></ProtectedRoute>} />
                        <Route path="/teacher/accounts/create" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><Signup /></ProtectedRoute>} />
                        <Route path="/teacher/music/create" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><AddMusic /></ProtectedRoute>} />
                        <Route path="/teacher/music/manage" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><AddMusic /></ProtectedRoute>} />
                        <Route path="/teacher/leaderboard" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><LearningLeaderboard /></ProtectedRoute>} />
                        <Route path="/teacher/class-materials" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><AdminClassMaterials /></ProtectedRoute>} />

                        <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={["admin"]}><ManagementDashboard /></ProtectedRoute>} />
                        <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={["admin"]}><WeeklyReport /></ProtectedRoute>} />
                        <Route path="/admin/accounts" element={<ProtectedRoute allowedRoles={["admin"]}><AccountManagement /></ProtectedRoute>} />
                        <Route path="/admin/accounts/create-staff" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCreateStaff /></ProtectedRoute>} />
                        <Route path="/admin/accounts/import" element={<ProtectedRoute allowedRoles={["admin"]}><AcademyStudentCsvImport /></ProtectedRoute>} />
                        <Route path="/admin/leaderboard" element={<ProtectedRoute allowedRoles={["admin"]}><LearningLeaderboard /></ProtectedRoute>} />
                        <Route path="/admin/rewards" element={<ProtectedRoute allowedRoles={["admin"]}><RewardsAdmin /></ProtectedRoute>} />
                        <Route path="/admin/boss-preview" element={<ProtectedRoute allowedRoles={["admin"]}><TeamBossSpeakingPreview /></ProtectedRoute>} />
                        <Route path="/admin/navbar" element={<Navigate to="/admin/catalog" replace />} />
                        <Route path="/admin/links" element={<ProtectedRoute allowedRoles={["admin"]}><LinkAdmin /></ProtectedRoute>} />
                        <Route path="/admin/membership" element={<ProtectedRoute allowedRoles={["admin"]}><MembershipAdmin /></ProtectedRoute>} />
                        <Route path="/admin/api-usage" element={<ProtectedRoute allowedRoles={["admin"]}><ApiUsageAdmin /></ProtectedRoute>} />
                        <Route path="/admin/levels" element={<ProtectedRoute allowedRoles={["admin"]}><LevelAdmin /></ProtectedRoute>} />
                        <Route path="/admin/catalog" element={<ProtectedRoute allowedRoles={["admin"]}><CatalogAdmin /></ProtectedRoute>} />
                        <Route path="/admin/support" element={<ProtectedRoute allowedRoles={["admin"]}><AdminSupport /></ProtectedRoute>} />
                        <Route path="/admin/class-materials" element={<ProtectedRoute allowedRoles={["admin"]}><AdminClassMaterials /></ProtectedRoute>} />
                        <Route path="/admin/material-packages" element={<ProtectedRoute allowedRoles={["admin"]}><AdminMaterialPackages /></ProtectedRoute>} />
                        <Route path="/admin/store-orders" element={<ProtectedRoute allowedRoles={["admin"]}><AdminStoreOrders /></ProtectedRoute>} />
                        <Route path="/admin/student-lifecycle" element={<ProtectedRoute allowedRoles={["admin"]}><AdminStudentLifecycle /></ProtectedRoute>} />
                        <Route path="/admin/speaking-content" element={<ProtectedRoute allowedRoles={["admin"]}><SpeakingContentAdmin /></ProtectedRoute>} />
                    </Route>

                    <Route path="/userinfo" element={<RoleHomeRedirect />} />
                    <Route path="/teacher/add-music" element={<Navigate to="/teacher/music/create" replace />} />
                    <Route path="/teacher/students" element={<Navigate to="/teacher/accounts/create" replace />} />
                    <Route path="/home/playlist/addmusic" element={<Navigate to="/teacher/music/create" replace />} />
                    <Route path="/home/playlist/signup" element={<Navigate to="/teacher/accounts/create" replace />} />
                    <Route path="/home/playlist/:playlistId" element={<LegacyPlaylistRedirect />} />
                    <Route path="/teacher/navbar" element={<Navigate to="/admin/navbar" replace />} />
                    <Route path="/editnavbar" element={<Navigate to="/admin/navbar" replace />} />
                    <Route path="/signup" element={<Navigate to="/teacher/accounts/create" replace />} />
                    <Route path="/linksadmin" element={<Navigate to="/admin/links" replace />} />

                    <Route path="*" element={<NotFound />} />
                </Routes>
                </RouteErrorBoundary>
                </StoreProvider>
            </AuthProvider>
        </Router>
    );
};

export default App;
